import { usePostHog } from "posthog-js/react";
import { useCallback, useState } from "react";
import { DeviceAuthenticationData, UnitType } from "use-smart-locks-shared";
import { Sentry } from "../utils/sentry";
import {
  bluetoothIlockitService,
  LockState,
} from "./bluetooth-ilockit.service";

/**
 * Maximum time to wait for a lock operation (open/close) to complete.
 */
const LOCK_OPERATION_TIMEOUT_MS = 20_000;

/**
 * Interval between polling attempts to check lock status during operations.
 */
const LOCK_STATUS_POLL_INTERVAL_MS = 500;

const getLockTimeoutErrorMessage = (
  action: "open" | "close",
  unitType?: UnitType,
) => {
  const actionText = action === "open" ? "geöffnet" : "geschlossen";
  const baseMessage = `Schloss konnte nicht ${actionText} werden: Zeitüberschreitung.`;

  if (unitType === "e-bike") {
    return `${baseMessage} Bitte prüfen Sie, ob das Fahrrad vom Strom getrennt ist.`;
  }

  return baseMessage;
};

type UseBluetoothLockReturn = {
  isLoading: boolean;
  updateDeviceAuthenticationData: (
    deviceName: string,
    authenticationData: DeviceAuthenticationData,
  ) => void;
  openLock: (lockName: string, unitType?: UnitType) => Promise<void>;
  closeLock: (lockName: string, unitType?: UnitType) => Promise<void>;
  readLockStatus: (lockName: string) => Promise<LockState>;
};

type UseBluetoothLockOptions = {
  stationSlug?: string;
};

export const useBluetoothLock = (
  options?: UseBluetoothLockOptions,
): UseBluetoothLockReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();

  const logMessage = useCallback(
    (message: string, extra: Record<string, string>) => {
      const properties = {
        ...extra,
        ...(options?.stationSlug ? { stationSlug: options.stationSlug } : {}),
      };

      Sentry.captureMessage(message, {
        extra: properties,
        level: "debug",
      });
      posthog.capture(message, properties);
    },
    [options?.stationSlug, posthog],
  );

  // useCallback is used to prevent unnecessary re-renders
  const updateDeviceAuthenticationData = useCallback(
    (deviceName: string, authenticationData: DeviceAuthenticationData) => {
      bluetoothIlockitService.updateDeviceAuthenticationData(
        deviceName,
        authenticationData,
      );
    },
    [],
  );

  const openLock = useCallback(
    async (lockName: string, unitType?: UnitType) => {
      setIsLoading(true);
      try {
        await bluetoothIlockitService.openLock(lockName);

        const startTime = Date.now();

        while (Date.now() - startTime < LOCK_OPERATION_TIMEOUT_MS) {
          const status = await bluetoothIlockitService.readLockStatus(lockName);

          switch (status) {
            case LockState.Open:
              logMessage("Bluetooth lock successfully opened", { lockName });
              return;
            case LockState.BoltBlockedPreventedOpening:
              throw new Error(
                "Schloss kann nicht geöffnet werden: Riegel blockiert",
              );
            default:
              await new Promise((resolve) =>
                setTimeout(resolve, LOCK_STATUS_POLL_INTERVAL_MS as number),
              );
              break;
          }
        }

        throw new Error(getLockTimeoutErrorMessage("open", unitType));
      } finally {
        setIsLoading(false);
      }
    },
    [logMessage],
  );

  const closeLock = useCallback(
    async (lockName: string, unitType?: UnitType) => {
      setIsLoading(true);
      try {
        await bluetoothIlockitService.closeLock(lockName);

        const startTime = Date.now();

        while (Date.now() - startTime < LOCK_OPERATION_TIMEOUT_MS) {
          const status = await bluetoothIlockitService.readLockStatus(lockName);

          switch (status) {
            case LockState.Closed:
              logMessage("Bluetooth lock successfully closed", { lockName });
              return;
            case LockState.BoltBlockedPreventedClosing:
              throw new Error(
                "Schloss kann nicht geschlossen werden: Riegel blockiert",
              );
            case LockState.MovementPreventedClosing:
              throw new Error(
                "Schloss kann nicht geschlossen werden: Bewegung erkannt",
              );
            default:
              await new Promise((resolve) =>
                setTimeout(resolve, LOCK_STATUS_POLL_INTERVAL_MS as number),
              );
              break;
          }
        }

        throw new Error(getLockTimeoutErrorMessage("close", unitType));
      } finally {
        setIsLoading(false);
      }
    },
    [logMessage],
  );

  const readLockStatus = useCallback(async (lockName: string) => {
    return bluetoothIlockitService.readLockStatus(lockName);
  }, []);

  return {
    isLoading,
    updateDeviceAuthenticationData,
    openLock,
    closeLock,
    readLockStatus,
  };
};
